import type { ColumnDefinition } from "./columnSettings";

const brokerMarkColumn = {
  key: "broker_mark",
  label: "Br",
  pickerLabel: "Broker",
  colClass: "sfe-mark-col",
  colWidth: "22.4px",
  headerHtml:
    '<th class="sfe-mark-col" data-col="broker_mark" data-sort="broker_mark" data-type="str" data-tip="Broker. RH is Robinhood, TT Tastytrade, WB Webull, FD Fidelity. Show, hide, or move it in column settings.">Br</th>',
} satisfies ColumnDefinition;

const accountShortColumn = {
  key: "account_short",
  label: "Ac",
  pickerLabel: "Account",
  colClass: "sfe-mark-col sfe-acct-col",
  colWidth: "29.4px",
  headerHtml:
    '<th class="sfe-mark-col sfe-acct-col" data-col="account_short" data-sort="account_short" data-type="str" data-tip="Short account name from Settings. It starts as a shortened account name and you can rename it.">Ac</th>',
} satisfies ColumnDefinition;

export const TABLE_COLUMNS = {
  "agents-paper-options": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-agents-ticker-col",
      colWidth: "4%",
      headerHtml: '<th class="tk-cell" data-col="ticker" data-tip="Underlying symbol for the leg the agent holds, with a Why button carrying the agent\'s justification.">Ticker</th>',
    },
    { key: "side", label: "Side", headerHtml: '<th data-col="side" data-tip="Put or call badge for this leg.">Side</th>' },
    { key: "sector", label: "Sector", headerHtml: '<th data-col="sector" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Sector</th>', compactHidden: true },
    { key: "sfe_holding_avg_cost", label: "Avg cost", headerHtml: '<th class="num sfe-avg-cost-header" data-col="sfe_holding_avg_cost" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Avg cost</th>', compactHidden: true },
    { key: "strike", label: "Strike", headerHtml: '<th class="num" data-col="strike" data-tip="Contract strike, with the model PoP stacked underneath.">Strike</th>' },
    { key: "sfe_risk_opty", label: "Risk / opty", headerHtml: '<th class="num sfe-risk-opty-header" data-col="sfe_risk_opty" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Risk / opty</th>' },
    { key: "price", label: "Price", headerHtml: '<th class="num" data-col="price" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Price</th>' },
    { key: "target", label: "Target", headerHtml: '<th class="num" data-col="target" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Target</th>' },
    { key: "expiration", label: "Expiry", headerHtml: '<th data-col="expiration" data-tip="Contract expiration date, with days to expiration on the subline.">Expiry</th>' },
    { key: "dte", label: "DTE", headerHtml: '<th class="num" data-col="dte" data-tip="Days to expiration.">DTE</th>', sublineOf: "expiration", pickerHidden: true },
    { key: "qty", label: "Qty", headerHtml: '<th class="num" data-col="qty" data-tip="Contracts the agent holds on this leg.">Qty</th>' },
    { key: "entry", label: "Entry", headerHtml: '<th class="num" data-col="entry" data-tip="Premium per share the agent sold this leg for.">Entry</th>' },
    { key: "credit", label: "Credit", headerHtml: '<th class="num" data-col="credit" data-tip="Premium received at open (entry × 100 × contracts).">Credit</th>' },
    { key: "mark", label: "Mid", headerHtml: '<th class="num" data-col="mark" data-tip="Current option mid (bid/ask midpoint from quotes). Used for open P/L and management decisions.">Mid</th>', sublineOf: "entry" },
    { key: "pnl", label: "P/L", headerHtml: '<th class="num" data-col="pnl" data-tip="Unrealized P/L on the leg: credit received minus the cost to close at the current mid.">P/L</th>' },
    { key: "held_roc", label: "Held ROC", headerHtml: '<th class="num" data-col="held_roc" data-tip="Return on collateral right now: (credit + unrealized P/L) ÷ collateral. Moves with the mid.">Held ROC</th>', compactHidden: true },
    { key: "open_roc", label: "Open ROC", headerHtml: '<th class="num" data-col="open_roc" data-tip="Return on collateral booked at open: credit ÷ collateral. Fixed once the leg is sold.">Open ROC</th>' },
    { key: "collateral", label: "Collateral", headerHtml: '<th class="num" data-col="collateral" data-tip="Cash secured for short puts (strike × 100 × contracts). Calls show — because shares back them.">Collateral</th>', sublineOf: "credit" },
    { key: "actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="actions"></th>' },
  ] satisfies ColumnDefinition[],

  "agents-paper-options-compact": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-agents-ticker-col",
      colWidth: "4%",
      headerHtml: '<th class="tk-cell" data-col="ticker" data-tip="Underlying symbol for the leg the agent holds, with a Why button carrying the agent\'s justification.">Ticker</th>',
    },
    { key: "side", label: "Side", headerHtml: '<th data-col="side" data-tip="Put or call badge for this leg.">Side</th>' },
    { key: "strike", label: "Strike", headerHtml: '<th class="num" data-col="strike" data-tip="Contract strike, with the model PoP stacked underneath.">Strike</th>' },
    { key: "sfe_risk_opty", label: "Risk / opty", headerHtml: '<th class="num sfe-risk-opty-header" data-col="sfe_risk_opty" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Risk / opty</th>' },
    { key: "price", label: "Price / cost", headerHtml: '<th class="num" data-col="price" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Price / cost</th>', pickerLabel: "Price" },
    { key: "target", label: "Target", headerHtml: '<th class="num" data-col="target" data-tip="Not tracked for agent positions — kept so the layout matches the Options tab.">Target</th>' },
    { key: "expiration", label: "Expiry", headerHtml: '<th data-col="expiration" data-tip="Contract expiration date, with days to expiration on the subline.">Expiry</th>' },
    { key: "qty", label: "Qty", headerHtml: '<th class="num" data-col="qty" data-tip="Contracts the agent holds on this leg.">Qty</th>' },
    { key: "entry", label: "Entry / mid", headerHtml: '<th class="num" data-col="entry" data-tip="Premium per share the agent sold this leg for. Subline: current option mid.">Entry / mid</th>', pickerLabel: "Entry" },
    { key: "credit", label: "Credit / col", headerHtml: '<th class="num" data-col="credit" data-tip="Premium received at open (entry × 100 × contracts). Subline: col = cash secured for short puts; calls show n/a.">Credit / col</th>', pickerLabel: "Credit" },
    { key: "pnl", label: "P/L", headerHtml: '<th class="num" data-col="pnl" data-tip="Unrealized P/L on the leg: credit received minus the cost to close at the current mid. Subline: the same P/L as a percent of credit.">P/L</th>' },
    { key: "open_roc", label: "Open ROC", headerHtml: '<th class="num" data-col="open_roc" data-tip="Return on collateral booked at open: credit ÷ collateral. Fixed once the leg is sold.">Open ROC</th>' },
    { key: "actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="actions"></th>' },
  ] satisfies ColumnDefinition[],

  "agents-paper-transactions": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-agents-ticker-col",
      colWidth: "4%",
      headerHtml: '<th class="tk-cell" data-col="ticker">Ticker</th>',
    },
    { key: "when", label: "When" },
    { key: "action", label: "Action" },
    { key: "strategy", label: "Strategy" },
    { key: "reason", label: "Reason" },
    { key: "side", label: "Side" },
    { key: "contract", label: "Contract" },
    { key: "qty", label: "Qty", headerClass: "num" },
    { key: "premium", label: "Premium", headerClass: "num" },
    { key: "pnl", label: "P&L", headerClass: "num" },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-puts-screener": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "underlying_ticker", label: "Ticker", locked: true },
    { key: "market_cap", label: "Cap", headerClass: "num" },
    { key: "sector", label: "Sector" },
    { key: "strike", label: "Strike", headerClass: "num", locked: true },
    { key: "underlying_price", label: "Price", headerClass: "num" },
    { key: "target_price", label: "Target", headerClass: "num", sublineOf: "underlying_price" },
    { key: "decision", label: "Rubric band", pickerLabel: "Rubric" },
    { key: "expiration_date", label: "Expiry", locked: true },
    { key: "next_earnings_date", label: "Earnings" },
    { key: "dte", label: "DTE", headerClass: "num", sublineOf: "expiration_date", pickerHidden: true },
    { key: "mid", label: "Mid", headerClass: "num" },
    { key: "last_price", label: "Last", headerClass: "num", sublineOf: "mid" },
    { key: "rec_quantity", label: "Size", headerClass: "num" },
    { key: "collateral", label: "Collat", headerClass: "num" },
    { key: "roi", label: "Ann. ROC", headerClass: "num", pickerLabel: "Ann ROC" },
    { key: "pop", label: "PoP", headerClass: "num", sublineOf: "strike" },
    { key: "held", label: "Shares", headerClass: "num held-th", locked: true },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
  ] satisfies ColumnDefinition[],

  /** Puts+ — MD primary; SF enrich columns get orange dots in the controller. */
  "web-puts-plus-screener": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "underlying_ticker", label: "Ticker", locked: true },
    { key: "market_cap", label: "Cap", headerClass: "num" },
    { key: "sector", label: "Sector" },
    { key: "strike", label: "Strike", headerClass: "num", locked: true },
    { key: "underlying_price", label: "Price", headerClass: "num" },
    { key: "target_price", label: "Target", headerClass: "num", sublineOf: "underlying_price" },
    { key: "decision", label: "Rubric band", pickerLabel: "Rubric" },
    { key: "expiration_date", label: "Expiry", locked: true },
    { key: "next_earnings_date", label: "Earnings" },
    { key: "dte", label: "DTE", headerClass: "num", sublineOf: "expiration_date", pickerHidden: true },
    { key: "mid", label: "Mid", headerClass: "num" },
    { key: "last_price", label: "Last", headerClass: "num", sublineOf: "mid" },
    { key: "collateral", label: "Collat", headerClass: "num" },
    { key: "roi", label: "Ann. ROC", headerClass: "num", pickerLabel: "Ann ROC" },
    { key: "pop", label: "PoP", headerClass: "num", sublineOf: "roi" },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-puts-plus-screener-compact": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "underlying_ticker", label: "Ticker", locked: true },
    { key: "strike", label: "Strike", headerClass: "num", locked: true },
    { key: "underlying_price", label: "Price / tgt", headerClass: "num", pickerLabel: "Price" },
    { key: "decision", label: "Rubric" },
    { key: "expiration_date", label: "Expiry", locked: true },
    { key: "next_earnings_date", label: "Earnings" },
    { key: "mid", label: "Mid / last", headerClass: "num", pickerLabel: "Mid" },
    { key: "collateral", label: "Collat", headerClass: "num" },
    { key: "roi", label: "Ann / PoP", headerClass: "num", pickerLabel: "Ann ROC" },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-calls-screener": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "underlying_ticker", label: "Ticker", locked: true },
    { key: "holding_avg_cost", label: "Avg cost", headerClass: "num" },
    { key: "strike", label: "Strike", headerClass: "num" },
    { key: "underlying_price", label: "Price", headerClass: "num" },
    { key: "target_price", label: "Target", headerClass: "num", sublineOf: "underlying_price" },
    { key: "expiration_date", label: "Expiry" },
    { key: "next_earnings_date", label: "Earnings" },
    { key: "dte", label: "DTE", headerClass: "num", sublineOf: "expiration_date", pickerHidden: true },
    { key: "mid", label: "Mid", headerClass: "num" },
    { key: "last_price", label: "Last", headerClass: "num", sublineOf: "mid" },
    { key: "available_call_contracts", label: "Size", headerClass: "num" },
    { key: "pop", label: "PoP", headerClass: "num", sublineOf: "strike" },
    { key: "sfe_profitability", label: "Profit.", headerClass: "num" },
    { key: "open", label: "Shares", headerClass: "num held-th", locked: true },
    { key: "market_cap", label: "Cap", headerClass: "num" },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
    { key: "sector", label: "Sector" },
    { key: "decision", label: "Rubric band", pickerLabel: "Rubric" },
    { key: "collateral", label: "Collat", headerClass: "num" },
    { key: "roi", label: "Ann. ROC", headerClass: "num", pickerLabel: "Ann ROC" },
  ] satisfies ColumnDefinition[],

  "web-options-positions": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-options-ticker-col",
      colWidth: "6.2%",
      headerHtml:
        '<th class="tk-cell" data-sort="ticker" data-col="ticker" data-tip="Underlying symbol with its moneyness badge (ITM / ATM) and the put or call tag. Click the ticker for analysis + heatmap.">Ticker</th>',
    },
    { key: "sector", label: "Sector", headerHtml: '<th data-sort="sector" data-col="sector" data-tip="Sector for the underlying, from Robinhood fundamentals when available.">Sector</th>', compactHidden: true },
    { key: "sfe_holding_avg_cost", label: "Avg cost", headerHtml: '<th class="num sfe-avg-cost-header" data-col="sfe_holding_avg_cost" data-tip="Average share cost basis for covered calls (own ledger: assignment lots use strike; option premiums excluded).">Avg cost</th>', compactHidden: true },
    { key: "strike", label: "Strike", headerHtml: '<th class="num" data-sort="strike" data-col="strike" data-tip="Contract strike — calls sell 100 shares per contract here if assigned, puts buy 100 shares per contract here.">Strike</th>' },
    { key: "sfe_risk_opty", label: "Risk / opty", headerHtml: '<th class="num sfe-risk-opty-header" data-col="sfe_risk_opty" data-tip="Calls: dollars at risk if strike is below avg share cost, or opportunity if strike is above. Puts: —.">Risk / opty</th>' },
    {
      key: "price",
      label: "Price",
      headerHtml:
        '<th class="num" data-sort="price" data-col="price" data-tip="Underlying mark from Robinhood when available.">Price</th>',
    },
    {
      key: "target",
      label: "Target",
      headerHtml:
        '<th class="num" data-sort="target" data-col="target" data-tip="Target price from Stock Forge.">Target</th>',
    },
    { key: "open_date", label: "Open", headerHtml: '<th data-sort="open_date" data-col="open_date" data-tip="Date the leg was sold to open, from the oldest still-open STO lot in your own ledger. Subline: calendar days the contract has been open. Shows — when a position arrives from Robinhood without an open date.">Open</th>' },
    { key: "expiration", label: "Expiry", headerHtml: '<th data-sort="expiration" data-col="expiration" data-tip="Contract expiration date, with days to expiration on the subline.">Expiry</th>' },
    { key: "dte", label: "DTE", headerHtml: '<th class="num" data-sort="dte" data-col="dte" data-tip="Days to expiration.">DTE</th>', sublineOf: "expiration", pickerHidden: true },
    { key: "qty", label: "Qty", colClass: "sfe-options-qty-col", colWidth: "3%", headerHtml: '<th class="num" data-sort="qty" data-col="qty" data-tip="Open contracts on this leg.">Qty</th>' },
    {
      key: "entry",
      label: "Entry",
      headerHtml:
        '<th class="num" data-sort="entry" data-col="entry" data-tip="Average short entry from own-ledger STO lots.">Entry</th>',
    },
    { key: "credit", label: "Credit", headerHtml: '<th class="num" data-sort="credit" data-col="credit" data-tip="Premium received when the leg was opened (entry × 100 × contracts).">Credit</th>' },
    {
      key: "mark",
      label: "Mid",
      headerHtml:
        '<th class="num" data-sort="mark" data-col="mark" data-tip="Option mid (bid/ask midpoint) from Robinhood when available; otherwise Stock Forge. Used for open P/L.">Mid</th>',
      sublineOf: "entry",
    },
    {
      key: "pnl",
      label: "P/L",
      headerHtml:
        '<th class="num" data-sort="pnl" data-col="pnl" data-tip="Unrealized P/L from entry vs mid (own-ledger entry × RH/SF mid).">P/L</th>',
    },
    { key: "held_roc", label: "Held ROC", headerHtml: '<th class="num" data-sort="held_roc" data-col="held_roc" data-tip="Return on capital you are holding right now: (credit + unrealized P/L) ÷ that capital. Puts use cash collateral (strike × 100 × contracts). Calls use share cost (avg cost × 100 × contracts). Moves with the mid.">Held ROC</th>', compactHidden: true },
    { key: "open_roc", label: "Open ROC", headerHtml: '<th class="num" data-sort="open_roc" data-col="open_roc" data-tip="Return on capital booked at open: credit ÷ that capital. Puts use cash collateral (strike × 100 × contracts). Calls use share cost (avg cost × 100 × contracts). Fixed once the leg is sold. Calls show — if avg cost is missing.">Open ROC</th>' },
    { key: "collateral", label: "Collateral", headerHtml: '<th class="num" data-sort="collateral" data-col="collateral" data-tip="Cash secured for short puts (strike × 100 × contracts). Calls show — because shares, not cash, back them.">Collateral</th>', sublineOf: "credit" },
    { key: "actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-stocks-positions": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-stocks-ticker-col",
      colWidth: "5.5%",
      headerHtml:
        '<th class="tk-cell" data-sort="ticker" data-col="ticker" data-tip="Share position symbol. Click the ticker for analysis + heatmap.">Ticker</th>',
    },
    {
      key: "sector",
      label: "Sector",
      headerHtml:
        '<th data-sort="sector" data-col="sector" data-tip="Sector from Robinhood fundamentals (falls back to Stock Forge if missing).">Sector</th>',
      compactHidden: true,
    },
    { key: "quantity", label: "Shares", headerHtml: '<th class="num" data-sort="quantity" data-col="quantity" data-tip="Shares held per own ledger. Subline: avail = shares not tied to open short call contracts.">Shares</th>' },
    {
      key: "sfe_shares_available",
      label: "Available",
      headerHtml:
        '<th class="num sfe-stocks-available-header" data-col="sfe_shares_available" data-tip="Own-ledger shares minus open short call contracts in the own ledger (held − shares under calls).">Available</th>',
      sublineOf: "quantity",
      pickerHidden: true,
    },
    {
      key: "avg_price",
      label: "Avg cost",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-sort="avg_price" data-col="avg_price" data-tip="Strike-based share avg from own ledger (assignment lots use strike; option premiums excluded).">Avg cost</th>',
    },
    {
      key: "adjusted_avg",
      label: "Adj avg",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-sort="adjusted_avg" data-col="adjusted_avg" data-tip="Own ledger: premium-adjusted share avg (assignment lots use strike − premiums / tax-lot cost).">Adj avg</th>',
    },
    {
      key: "days_held",
      label: "Days held",
      headerHtml:
        '<th class="num" data-sort="days_held" data-col="days_held" data-tip="Calendar days since the oldest open share lot in own ledger. Lot age only — cost basis is the strike-based average, never FIFO.">Days held</th>',
      sublineOf: "avg_price",
    },
    {
      key: "price",
      label: "Price",
      headerHtml:
        '<th class="num" data-sort="price" data-col="price" data-tip="Mark price from Robinhood MCP.">Price</th>',
    },
    {
      key: "target",
      label: "Target",
      headerHtml:
        '<th class="num" data-sort="target" data-col="target" data-tip="Target price from Stock Forge.">Target</th>',
      sublineOf: "price",
    },
    { key: "cost_basis", label: "Cost basis", headerHtml: '<th class="num" data-sort="cost_basis" data-col="cost_basis" data-tip="Own-ledger cost of the shares you still hold (shares × avg cost).">Cost basis</th>' },
    {
      key: "market_value",
      label: "Mkt value",
      headerHtml:
        '<th class="num" data-sort="market_value" data-col="market_value" data-tip="Own-ledger shares × Robinhood mark price.">Mkt value</th>',
      sublineOf: "cost_basis",
    },
    {
      key: "pl_dollar",
      label: "P/L $",
      headerHtml:
        '<th class="num" data-sort="pl_dollar" data-col="pl_dollar" data-tip="Market value (RH mark) minus own-ledger cost basis.">P/L $</th>',
    },
    {
      key: "pl_pct",
      label: "P/L %",
      headerHtml:
        '<th class="num" data-sort="pl_pct" data-col="pl_pct" data-tip="P/L $ as a percent of own-ledger cost basis (uses Robinhood mark).">P/L %</th>',
      sublineOf: "pl_dollar",
    },
    { key: "actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-puts-screener-compact": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "underlying_ticker", label: "Ticker", locked: true },
    { key: "strike", label: "Strike / PoP", headerClass: "num", locked: true, pickerLabel: "Strike" },
    { key: "underlying_price", label: "Price / tgt", headerClass: "num", pickerLabel: "Price" },
    { key: "decision", label: "Rubric" },
    { key: "expiration_date", label: "Expiry", locked: true },
    { key: "next_earnings_date", label: "Earnings" },
    { key: "mid", label: "Mid / last", headerClass: "num", pickerLabel: "Mid" },
    { key: "collateral", label: "Collat", headerClass: "num" },
    { key: "roi", label: "Ann / $ per day", headerClass: "num", pickerLabel: "Ann ROC" },
    { key: "held", label: "Shares", headerClass: "num held-th", locked: true },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-calls-screener-compact": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "underlying_ticker", label: "Ticker", locked: true },
    { key: "strike", label: "Strike / PoP", headerClass: "num", pickerLabel: "Strike" },
    { key: "underlying_price", label: "Price / tgt", headerClass: "num", pickerLabel: "Price" },
    { key: "decision", label: "Rubric" },
    { key: "expiration_date", label: "Expiry" },
    { key: "next_earnings_date", label: "Earnings" },
    { key: "mid", label: "Mid / last", headerClass: "num", pickerLabel: "Mid" },
    { key: "holding_avg_cost", label: "Avg / avail", headerClass: "num", pickerLabel: "Avg cost" },
    { key: "sfe_profitability", label: "Profit / Ann", headerClass: "num", pickerLabel: "Profit" },
    { key: "open", label: "Shares", headerClass: "num held-th", locked: true },
    { key: "_actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="_actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-options-positions-compact": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-options-ticker-col",
      colWidth: "6.2%",
      headerHtml:
        '<th class="tk-cell" data-col="ticker" data-tip="Underlying symbol with its moneyness badge (ITM / ATM) and the put or call tag. Click the ticker for analysis + heatmap.">Ticker</th>',
    },
    { key: "strike", label: "Strike", headerHtml: '<th class="num" data-col="strike" data-tip="Contract strike — calls sell 100 shares per contract here if assigned, puts buy 100 shares per contract here.">Strike</th>' },
    { key: "sfe_risk_opty", label: "Risk / opty", headerHtml: '<th class="num sfe-risk-opty-header" data-col="sfe_risk_opty" data-tip="Calls: dollars at risk if strike is below avg share cost, or opportunity if strike is above. Puts: —.">Risk / opty</th>' },
    { key: "price", label: "Price / cost", headerHtml: '<th class="num" data-col="price" data-tip="Underlying mark from Robinhood. Subline: avg = your average share cost on calls; puts show —.">Price / cost</th>', pickerLabel: "Price" },
    { key: "target", label: "Target", headerHtml: '<th class="num" data-col="target" data-tip="Target price from Stock Forge.">Target</th>' },
    { key: "open_date", label: "Open", headerHtml: '<th data-col="open_date" data-tip="Date the leg was sold to open, from the oldest still-open STO lot in your own ledger. Subline: calendar days the contract has been open. Shows — when a position arrives from Robinhood without an open date.">Open</th>' },
    { key: "expiration", label: "Expiry", headerHtml: '<th data-col="expiration" data-tip="Contract expiration date, with days to expiration on the subline.">Expiry</th>' },
    { key: "qty", label: "Qty", colClass: "sfe-options-qty-col", colWidth: "3%", headerHtml: '<th class="num" data-col="qty" data-tip="Open contracts on this leg.">Qty</th>' },
    { key: "entry", label: "Entry / mid", headerHtml: '<th class="num" data-col="entry" data-tip="Average short entry from own-ledger STO lots. Subline: current option mid (bid/ask midpoint).">Entry / mid</th>', pickerLabel: "Entry" },
    { key: "credit", label: "Credit / col", headerHtml: '<th class="num" data-col="credit" data-tip="Premium received at open (entry × 100 × contracts). Subline: col = cash secured for short puts; calls show —.">Credit / col</th>', pickerLabel: "Credit" },
    { key: "pnl", label: "P/L", headerHtml: '<th class="num" data-col="pnl" data-tip="Unrealized P/L from entry vs mid (own-ledger entry × RH/SF mid). Subline: the same P/L as a percent of premium collected.">P/L</th>' },
    { key: "open_roc", label: "Open ROC", headerHtml: '<th class="num" data-col="open_roc" data-tip="Return on capital booked at open: credit ÷ that capital. Puts use cash collateral (strike × 100 × contracts). Calls use share cost (avg cost × 100 × contracts). Fixed once the leg is sold. Calls show — if avg cost is missing.">Open ROC</th>' },
    { key: "actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-stocks-positions-compact": [
    brokerMarkColumn,
    accountShortColumn,
    {
      key: "ticker",
      label: "Ticker",
      locked: true,
      colClass: "sfe-stocks-ticker-col",
      colWidth: "5.5%",
      headerHtml:
        '<th class="tk-cell" data-col="ticker" data-tip="Share position symbol. Click the ticker for analysis + heatmap.">Ticker</th>',
    },
    {
      key: "sector",
      label: "Sector",
      headerHtml:
        '<th data-col="sector" data-tip="Sector from Robinhood fundamentals.">Sector</th>',
    },
    { key: "quantity", label: "Shares", headerHtml: '<th class="num" data-col="quantity" data-tip="Shares held per own ledger. Subline: avail = shares not tied to open short call contracts.">Shares</th>' },
    { key: "avg_price", label: "Avg cost", headerHtml: '<th class="num sfe-data-tip-host" data-col="avg_price" data-tip="Strike-based share avg from own ledger (assignment lots use strike; option premiums excluded). Subline: days since the oldest open lot.">Avg cost</th>' },
    {
      key: "adjusted_avg",
      label: "Adj avg",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="adjusted_avg" data-tip="Own ledger: premium-adjusted share avg (strike − premiums).">Adj avg</th>',
    },
    {
      key: "price",
      label: "Price / tgt",
      headerHtml:
        '<th class="num" data-col="price" data-tip="Mark from Robinhood MCP; target from Stock Forge.">Price / tgt</th>',
      pickerLabel: "Price",
    },
    { key: "cost_basis", label: "Cost basis", headerHtml: '<th class="num" data-col="cost_basis" data-tip="Own-ledger cost of the shares you still hold (shares × avg cost). Subline: mkt = current market value.">Cost basis</th>' },
    {
      key: "pl_dollar",
      label: "P/L",
      headerHtml:
        '<th class="num" data-col="pl_dollar" data-tip="Market value (RH mark) minus own-ledger cost basis. Subline: the same P/L as a percent of cost basis.">P/L</th>',
    },
    { key: "actions", label: "", locked: true, pickerHidden: true, headerHtml: '<th data-col="actions"></th>' },
  ] satisfies ColumnDefinition[],

  "web-history": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "ticker", label: "Ticker", headerHtml: '<th data-col="ticker" data-tip="Underlying symbol for the transaction.">Ticker</th>' },
    { key: "trade_date", label: "Date", headerHtml: '<th data-col="trade_date" data-tip="Trade date the transaction settled to in StockForge.">Date</th>' },
    { key: "instrument_type", label: "Type", headerHtml: '<th data-col="instrument_type" data-tip="Instrument: option, stock or cash movement.">Type</th>' },
    { key: "action", label: "Action", headerHtml: '<th data-col="action" data-tip="What happened: sell to open, buy to close, assignment, expiration, stock buy or sell, cash in or out.">Action</th>' },
    { key: "side", label: "Side", headerHtml: '<th data-col="side" data-tip="Put or call for option rows; blank for stock and cash rows.">Side</th>' },
    { key: "quantity", label: "Qty", headerHtml: '<th class="num" data-col="quantity" data-tip="Contracts for option rows, shares for stock rows.">Qty</th>' },
    { key: "avg_price", label: "Price", headerHtml: '<th class="num sfe-data-tip-host" data-col="avg_price" data-tip="Premium or price per share. Total cash = price × qty × 100 for options.">Price</th>' },
    {
      key: "holding_avg",
      label: "Avg cost",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="holding_avg" data-tip="Stock buys: holding average before → after this purchase. Stock sells: holding average before the sale.">Avg cost</th>',
    },
    { key: "strike", label: "Strike", headerHtml: '<th class="num" data-col="strike" data-tip="Option strike; blank for stock and cash rows.">Strike</th>' },
    { key: "exp_date", label: "Expiry", colWidth: "1%", headerHtml: '<th data-col="exp_date" data-tip="Option expiration date; blank for stock and cash rows.">Expiry</th>' },
    {
      key: "actions",
      label: "",
      locked: true,
      pickerHidden: true,
      colClass: "sfe-hist-actions-col",
      colWidth: "28px",
      headerHtml: '<th data-col="actions"></th>',
    },
  ] satisfies ColumnDefinition[],

  "web-history-local": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "ticker", label: "Ticker", headerHtml: '<th data-col="ticker" data-tip="Underlying symbol for the transaction.">Ticker</th>' },
    { key: "trade_date", label: "Date", headerHtml: '<th data-col="trade_date" data-tip="Trade date recorded in your own ledger.">Date</th>' },
    { key: "instrument_type", label: "Type", headerHtml: '<th data-col="instrument_type" data-tip="Instrument: option, stock or cash movement.">Type</th>' },
    { key: "action", label: "Action", headerHtml: '<th data-col="action" data-tip="What happened: sell to open, buy to close, assignment, expiration, stock buy or sell, cash in or out.">Action</th>' },
    { key: "system", label: "System", headerHtml: '<th data-col="system" data-tip="Where the row came from: Robinhood import, Tasty Trade import or manual entry.">System</th>' },
    { key: "account", label: "Account", headerHtml: '<th data-col="account" data-tip="Brokerage account the transaction belongs to.">Account</th>' },
    { key: "side", label: "Side", headerHtml: '<th data-col="side" data-tip="Put or call for option rows; blank for stock and cash rows.">Side</th>' },
    { key: "quantity", label: "Qty", headerHtml: '<th class="num" data-col="quantity" data-tip="Contracts for option rows, shares for stock rows.">Qty</th>' },
    { key: "avg_price", label: "Price", headerHtml: '<th class="num sfe-data-tip-host" data-col="avg_price" data-tip="Premium or price per share. Total cash = price × qty × 100 for options.">Price</th>' },
    {
      key: "holding_avg",
      label: "Avg cost",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="holding_avg" data-tip="Stock buys: holding average before → after this purchase. Stock sells: holding average before the sale.">Avg cost</th>',
    },
    {
      key: "share_avg",
      label: "Share avg",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="share_avg" data-tip="Call rows: average share cost backing the contract at that moment — the basis a call assignment is priced against.">Share avg</th>',
    },
    {
      key: "assign_pl",
      label: "Assign P/L",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="assign_pl" data-tip="Call assignments: (strike − share avg cost) × shares assigned away. Same figure Analytics(L) books in the assignment month.">Assign P/L</th>',
    },
    {
      key: "share_pos",
      label: "Position",
      headerHtml:
        '<th class="sfe-data-tip-host" data-col="share_pos" data-tip="On stock rows: share position with its holding average before → after this trade. On cash rows: the running external cash total (deposits, withdrawals, transfers and interest only — premiums and assignment proceeds are not cash flows). Blank on option rows, including assignments — those share the companion stock leg’s position instead of duplicating it.">Position</th>',
    },
    { key: "strike", label: "Strike", headerHtml: '<th class="num" data-col="strike" data-tip="Option strike; blank for stock and cash rows.">Strike</th>' },
    {
      key: "underlying_open_price",
      label: "Open px",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="underlying_open_price" data-tip="Share price of the underlying when the contract was sold to open, taken from the minute the order filled.">Open px</th>',
    },
    {
      key: "underlying_close_price",
      label: "Close px",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="underlying_close_price" data-tip="Share price of the underlying when the contract closed: the fill minute for a buy to close, or the session close for an expiration or assignment. A ≈ prefix means the fill minute was unavailable and the session close stands in.">Close px</th>',
    },
    {
      key: "open_pop",
      label: "POP at open",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="open_pop" data-tip="Probability of profit when the contract was sold (1 − |delta|), the same measure the screeners rank by, read shortly after the fill. Settlements show the odds their opening leg was written at. Blank for contracts opened before this was recorded — neither broker publishes historical greeks, so it cannot be filled in after the fact.">POP at open</th>',
    },
    { key: "exp_date", label: "Expiry", colWidth: "1%", headerHtml: '<th data-col="exp_date" data-tip="Option expiration date; blank for stock and cash rows.">Expiry</th>' },
    { key: "notes", label: "Notes", headerHtml: '<th data-col="notes" data-tip="Free-text note stored with the transaction in your own ledger.">Notes</th>' },
    {
      key: "actions",
      label: "",
      locked: true,
      pickerHidden: true,
      colClass: "sfe-hist-actions-col",
      colWidth: "28px",
      headerHtml: '<th data-col="actions"></th>',
    },
  ] satisfies ColumnDefinition[],

  "analytics-income-txn": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "type", label: "Type", locked: true, colWidth: "6%" },
    { key: "ticker", label: "Ticker", colWidth: "7%" },
    { key: "side", label: "Side", colWidth: "6%" },
    { key: "strike", label: "Strike", headerClass: "num", colWidth: "6%" },
    { key: "qty", label: "Qty", headerClass: "num", colWidth: "5%" },
    { key: "open", label: "Open", colWidth: "11%" },
    {
      key: "exp",
      label: "Exp",
      colWidth: "11%",
      headerHtml:
        '<th data-col="exp" data-tip="Option expiration date. Blank for stock and cash rows.">Exp</th>',
    },
    { key: "credit", label: "Credit / proceeds", headerClass: "num", colWidth: "10%" },
    { key: "btc", label: "BTC", headerClass: "num", colWidth: "6%" },
    {
      key: "cost",
      label: "Cost",
      headerClass: "num",
      colWidth: "7%",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="cost" data-tip="Options: share/contract cost. Stock buys: holding avg before → after this purchase.">Cost</th>',
    },
    { key: "txnId", label: "Txn ID", colWidth: "6%" },
    { key: "linkedId", label: "Linked ID", colWidth: "7%" },
    { key: "link", label: "Link", colWidth: "7%" },
    { key: "pl", label: "P/L", headerClass: "num", colWidth: "5%" },
  ] satisfies ColumnDefinition[],

  /** Analytics(L) income day tables — Broker / Account use the short display names. */
  "analytics-income-txn-local": [
    {
      key: "system",
      label: "Broker",
      colClass: "sfe-col-system",
      colWidth: "52px",
      headerHtml:
        '<th class="sfe-col-system sfe-mark-col" data-col="system" data-tip="Broker display name. RH is Robinhood, TT Tastytrade, WB Webull, FD Fidelity.">Broker</th>',
    },
    {
      key: "account",
      label: "Account",
      colClass: "sfe-col-account",
      colWidth: "58px",
      headerHtml:
        '<th class="sfe-col-account sfe-acct-col" data-col="account" data-tip="Short account name from Settings.">Account</th>',
    },
    { key: "type", label: "Type", locked: true, colWidth: "4.5%" },
    { key: "ticker", label: "Ticker", colWidth: "5.5%" },
    { key: "side", label: "Side", colWidth: "4.5%" },
    { key: "strike", label: "Strike", headerClass: "num", colWidth: "4.5%" },
    {
      key: "openPx",
      label: "Open px",
      headerClass: "num",
      colWidth: "5%",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="openPx" data-tip="Share price of the underlying when the contract was sold to open, taken from the minute the order filled.">Open px</th>',
    },
    {
      key: "closePx",
      label: "Close px",
      headerClass: "num",
      colWidth: "5%",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="closePx" data-tip="Share price of the underlying when the contract closed: the fill minute for a buy to close, or the session close for an expiration or assignment. A ≈ prefix means the fill minute was unavailable and the session close stands in.">Close px</th>',
    },
    {
      key: "openPop",
      label: "POP at open",
      headerClass: "num",
      colWidth: "5.5%",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="openPop" data-tip="Probability of profit when the contract was sold (1 − |delta|), the same measure the screeners rank by, read shortly after the fill. Settlements show the odds their opening leg was written at. Blank for contracts opened before this was recorded — neither broker publishes historical greeks, so it cannot be filled in after the fact.">POP at open</th>',
    },
    { key: "qty", label: "Qty", headerClass: "num", colWidth: "3.5%" },
    { key: "open", label: "Open", colWidth: "10%" },
    {
      key: "exp",
      label: "Exp",
      colWidth: "10%",
      headerHtml:
        '<th data-col="exp" data-tip="Option expiration date. Blank for stock and cash rows.">Exp</th>',
    },
    { key: "credit", label: "Credit / proceeds", headerClass: "num", colWidth: "8%" },
    { key: "btc", label: "BTC", headerClass: "num", colWidth: "6%" },
    {
      key: "cost",
      label: "Cost",
      headerClass: "num",
      colWidth: "5%",
      headerHtml:
        '<th class="num sfe-data-tip-host" data-col="cost" data-tip="Options: share/contract cost. Stock buys: holding avg before → after this purchase.">Cost</th>',
    },
    { key: "txnId", label: "Txn ID", colWidth: "4%" },
    { key: "linkedId", label: "Linked ID", colWidth: "4%" },
    { key: "link", label: "Link", colWidth: "5%" },
    { key: "pl", label: "P/L", headerClass: "num", colWidth: "5.5%" },
  ] satisfies ColumnDefinition[],

  "calls-plus-main": [
    { key: "expand", label: "Expand", locked: true, pickerHidden: true, colClass: "sfe-cp-col-expand", headerHtml: '<th class="sfe-calls-plus-expand-head" aria-label="Expand"></th>' },
    brokerMarkColumn,
    accountShortColumn,
    { key: "ticker", label: "Ticker", locked: true, colClass: "sfe-cp-col-ticker" },
    { key: "shares", label: "Shares", colClass: "sfe-cp-col-shares", headerClass: "num sfe-data-tip-host", headerHtml: '<th class="num sfe-data-tip-host" data-tip="Shares not tied to open short call contracts.">Shares</th>' },
    { key: "avgCost", label: "Avg cost", colClass: "sfe-cp-col-avgcost", headerClass: "num" },
    { key: "price", label: "Price", colClass: "sfe-cp-col-price", headerClass: "num" },
    { key: "atRisk", label: "At risk", colClass: "sfe-cp-col-risk", headerClass: "num" },
    { key: "even", label: "Even", colClass: "sfe-cp-col-even", headerClass: "num" },
    { key: "cumProfit", label: "Cum. profit", colClass: "sfe-cp-col-cumprofit", headerClass: "num" },
    { key: "strike", label: "Strike", colClass: "sfe-cp-col-strike", headerClass: "num" },
    { key: "pop", label: "POP", colClass: "sfe-cp-col-pop", headerClass: "num" },
    { key: "premium", label: "Premium", colClass: "sfe-cp-col-premium", headerClass: "num sfe-data-tip-host", headerHtml: '<th class="num sfe-data-tip-host" data-tip="Premium if sold as recommended at mid for all available shares: mid × contracts × 100.">Premium</th>' },
    { key: "expiry", label: "Expiry", colClass: "sfe-cp-col-expiry" },
    { key: "dte", label: "DTE", colClass: "sfe-cp-col-dte", headerClass: "num" },
    { key: "mark", label: "Mark", colClass: "sfe-cp-col-mark", headerClass: "num" },
    { key: "roi", label: "ROI", colClass: "sfe-cp-col-roi", headerClass: "num" },
    { key: "actions", label: "Actions", locked: true, pickerHidden: true, colClass: "sfe-cp-col-actions", headerHtml: '<th class="sfe-calls-plus-actions-head" aria-label="Actions"></th>' },
  ] satisfies ColumnDefinition[],

  "calls-plus-history": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "open", label: "Open", locked: true, colClass: "sfe-cp-hist-date" },
    { key: "close", label: "Close", colClass: "sfe-cp-hist-date" },
    { key: "status", label: "Status", colClass: "sfe-cp-hist-event" },
    { key: "strike", label: "Strike", colClass: "sfe-cp-hist-price", headerClass: "num" },
    { key: "expiry", label: "Expiry", colClass: "sfe-cp-hist-date" },
    { key: "qty", label: "Qty", colClass: "sfe-cp-hist-shares", headerClass: "num" },
    { key: "premium", label: "Premium", colClass: "sfe-cp-hist-price", headerClass: "num" },
    { key: "btc", label: "BTC", colClass: "sfe-cp-hist-price", headerClass: "num" },
    { key: "net", label: "Net", colClass: "sfe-cp-hist-price", headerClass: "num" },
  ] satisfies ColumnDefinition[],

  "calls-plus-ledger": [
    brokerMarkColumn,
    accountShortColumn,
    { key: "date", label: "Date", locked: true, colClass: "sfe-cp-hist-date" },
    { key: "event", label: "Event", colClass: "sfe-cp-hist-event" },
    { key: "shares", label: "Shares", colClass: "sfe-cp-hist-shares", headerClass: "num" },
    { key: "price", label: "Price", colClass: "sfe-cp-hist-price", headerClass: "num" },
    { key: "notional", label: "Notional", colClass: "sfe-cp-hist-notional", headerClass: "num" },
    { key: "netShares", label: "Net shares", colClass: "sfe-cp-hist-shares", headerClass: "num" },
    { key: "avgPrice", label: "Avg price", colClass: "sfe-cp-hist-price", headerClass: "num" },
  ] satisfies ColumnDefinition[],

  "tax-est-payments": [
    { key: "date", label: "Date", locked: true },
    { key: "amount", label: "Amount" },
    { key: "note", label: "Note" },
    { key: "actions", label: "Actions", locked: true, pickerHidden: true, headerHtml: '<th scope="col" aria-hidden="true"></th>' },
  ] satisfies ColumnDefinition[],
} as const;

export type TableColumnId = keyof typeof TABLE_COLUMNS;

export function getTableColumnDefaults(tableId: TableColumnId): ColumnDefinition[] {
  return TABLE_COLUMNS[tableId].map((column) => ({ ...column }));
}

export function tableColumnTitle(tableId: TableColumnId): string {
  switch (tableId) {
    case "web-puts-screener":
      return "Configure Puts columns";
    case "web-puts-plus-screener":
      return "Configure Puts+ columns";
    case "web-puts-plus-screener-compact":
      return "Configure Puts+ columns (compact)";
    case "web-puts-screener-compact":
      return "Configure Puts columns (compact)";
    case "web-calls-screener":
      return "Configure Calls columns";
    case "web-calls-screener-compact":
      return "Configure Calls columns (compact)";
    case "web-options-positions":
      return "Configure Options columns";
    case "web-options-positions-compact":
      return "Configure Options columns (compact)";
    case "web-stocks-positions":
      return "Configure Stocks columns";
    case "web-stocks-positions-compact":
      return "Configure Stocks columns (compact)";
    case "web-history":
      return "Configure History columns";
    case "web-history-local":
      return "Configure History columns";
    case "analytics-income-txn":
      return "Configure Analytics transaction columns";
    case "analytics-income-txn-local":
      return "Configure Analytics transaction columns";
    case "calls-plus-main":
      return "Configure Calls columns";
    case "calls-plus-history":
      return "Configure call history columns";
    case "calls-plus-ledger":
      return "Configure lot ledger columns";
    case "tax-est-payments":
      return "Configure payment columns";
    case "agents-paper-options":
      return "Configure Agent options columns";
    case "agents-paper-options-compact":
      return "Configure Agent options columns (compact)";
    case "agents-paper-transactions":
      return "Configure Agent transaction columns";
    default:
      return "Configure columns";
  }
}
